import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send } from "lucide-react";

// Modèles de message — communication individuelle depuis un dossier client
export const COMM_TEMPLATES = {
  hub_received: ["Colis reçu au Hub", (n, r) => `Bonjour ${n},\nVotre colis ${r} a bien été reçu au Hub Colis2Go. Nous procédons maintenant à la vérification.`],
  payment: ["Paiement demandé", (n, r) => `Bonjour ${n},\nVotre facture pour ${r} est disponible. Connectez-vous à votre espace Colis2Go pour la régler en ligne.`],
  departure: ["Départ confirmé", (n, r) => `Bonjour ${n},\nVotre colis ${r} a quitté le Hub et est maintenant en route. Merci pour votre confiance. Colis2Go`],
  delay: ["Retard", (n, r) => `Bonjour ${n},\nLe départ de votre colis ${r} est légèrement retardé. Nous vous informons dès la nouvelle date confirmée.`],
  arrival: ["Arrivée", (n, r) => `Bonjour ${n},\nVotre colis ${r} est arrivé à destination.`],
  available: ["Colis disponible", (n, r) => `Bonjour ${n},\nVotre colis ${r} est disponible au retrait. Présentez votre référence lors du retrait.`],
};

// Dialogue de communication individuelle : canaux WhatsApp / Email, modèle, message modifiable, photo.
export function CommDialog({ target, onClose }) {
  // target: { customer_id? , shipment_id?, purchase_id?, customer_name, reference, photos? }
  const first = (target.customer_name || "").split(" ")[0] || "client";
  const [tpl, setTpl] = useState("hub_received");
  const [msg, setMsg] = useState(COMM_TEMPLATES.hub_received[1](first, target.reference || ""));
  const [ch, setCh] = useState({ whatsapp: true, email: true });
  const [attach, setAttach] = useState(false);
  const [busy, setBusy] = useState(false);

  const pick = (k) => { setTpl(k); setMsg(COMM_TEMPLATES[k][1](first, target.reference || "")); };
  const send = async () => {
    const channels = Object.keys(ch).filter((k) => ch[k]);
    if (!channels.length) return toast.error("Choisissez au moins un canal");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/comms/send", {
        customer_id: target.customer_id, shipment_id: target.shipment_id, purchase_id: target.purchase_id,
        reference: target.reference, channels, message: msg,
        subject: `${COMM_TEMPLATES[tpl][0]}${target.reference ? " — " + target.reference : ""}`,
        attach_photo: attach, photos: attach ? (target.photos || []) : [],
      });
      if (data.email_sent) toast.success("Email envoyé au client");
      if (data.whatsapp_link) { window.open(data.whatsapp_link, "_blank"); toast.success("WhatsApp ouvert — message prérempli"); }
      else if (ch.whatsapp) toast.warning("Pas de numéro WhatsApp renseigné pour ce client");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="comm-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Envoyer une communication — {target.customer_name || target.reference}</DialogTitle>
          <DialogDescription>Choisissez le canal et le modèle, puis adaptez le message.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Modèle</Label>
            <select data-testid="comm-template" className="w-full h-9 border border-border bg-card px-2 text-sm" value={tpl} onChange={(e) => pick(e.target.value)}>
              {Object.entries(COMM_TEMPLATES).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <Checkbox data-testid="comm-ch-whatsapp" checked={ch.whatsapp} onCheckedChange={(v) => setCh({ ...ch, whatsapp: !!v })} /> WhatsApp
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <Checkbox data-testid="comm-ch-email" checked={ch.email} onCheckedChange={(v) => setCh({ ...ch, email: !!v })} /> Email
            </label>
            {(target.photos || []).length > 0 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="comm-attach" checked={attach} onCheckedChange={(v) => setAttach(!!v)} /> Joindre la photo
              </label>
            )}
          </div>
          <div>
            <Label className="text-xs">Message (modifiable)</Label>
            <textarea data-testid="comm-message" className="w-full min-h-[110px] border border-border bg-card p-2 text-sm" value={msg} onChange={(e) => setMsg(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="comm-send" disabled={busy} className="rounded-sm bg-accent text-white gap-1.5" onClick={send}>
            <Send className="h-4 w-4" /> Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
